/* @jsx h */
import { defineComponent, ref, h, computed, onMounted } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import { useMenu, useCustomer } from "@app/vue/providers/index";
import { createGroupOrder, fetchOrder } from "@app/server/client";
import { Order } from "@lemmonhq/types";
import { OrderReceipt } from "../../components/OrderReceipt/index";
import { getRestaurantImageURL } from "@lemmonhq/blogic";
import { LoadingComponent, useLocalization } from "@lemmonhq/uikit";

export const ShopCheckoutResultPage = defineComponent({
  name: "ShopCheckoutResultPage",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { shop, cart, groupOrder } = useMenu();
    const { customer } = useCustomer();
    const { t } = useLocalization();
    const createGroupLoading = ref(false);

    const orders = ref<Order[]>([]);
    const isLoading = ref(true);
    const error = ref<string | null>(null);

    const orderIds = computed(() => {
      // Try to get order IDs from query params first
      const orderIdsParam = route.query.orderIds as string;
      if (orderIdsParam) {
        return orderIdsParam.split(",").map((id) => parseInt(id.trim()));
      }

      // Fall back to getting order IDs from groupOrder checkouts
      if (!customer.value?.id || !groupOrder.value?.checkouts) {
        return [];
      }

      return [
        ...new Set(
          groupOrder.value.checkouts
            .filter((checkout) => checkout.customer_id === customer.value?.id)
            .map((checkout) => checkout.order_id)
            .filter(Boolean) as number[],
        ),
      ];
    });

    // Separate user's orders from other group orders
    const userOrders = computed(() => {
      if (!customer.value?.id || !groupOrder.value?.checkouts) {
        return orders.value;
      }

      const userOrderIds = [
        ...new Set(
          groupOrder.value.checkouts
            .filter((checkout) => checkout.customer_id === customer.value?.id)
            .map((checkout) => checkout.order_id)
            .filter(Boolean),
        ),
      ];

      return orders.value.filter((order) => userOrderIds.includes(order.id));
    });

    const otherOrders = computed(() => {
      if (!customer.value?.id || !groupOrder.value?.checkouts) {
        return [];
      }

      const userOrderIds = [
        ...new Set(
          groupOrder.value.checkouts
            .filter((checkout) => checkout.customer_id === customer.value?.id)
            .map((checkout) => checkout.order_id)
            .filter(Boolean),
        ),
      ];

      return orders.value.filter((order) => !userOrderIds.includes(order.id));
    });

    const fetchOrders = async () => {
      if (!shop.value?.slug) {
        error.value = t("customer.checkout_result.missing_shop");
        isLoading.value = false;
        return;
      }

      try {
        isLoading.value = true;

        // Get all order IDs from the group (not just user's orders)
        const allOrderIds = [
          ...new Set(
            groupOrder.value?.checkouts
              .map((checkout) => checkout.order_id)
              .filter(Boolean) as number[],
          ),
        ];

        if (!allOrderIds || allOrderIds.length === 0) {
          // No orders in the group yet
          orders.value = [];
          isLoading.value = false;
          return;
        }

        // Fetch all orders in parallel
        const orderPromises = allOrderIds.map((orderId) =>
          fetchOrder(shop.value!.slug, orderId),
        );
        orders.value = await Promise.all(orderPromises);
      } catch (err: any) {
        error.value = err.message || t("customer.checkout_result.fetch_failed");
      } finally {
        isLoading.value = false;
      }
    };

    const handleBackToMenu = () => {
      // Clear checkout state and return to menu
      cart.reset({
        checkoutState: "ordering",
      });
      router.push(`/restaurant/${shop.value?.slug}`);
    };

    onMounted(fetchOrders);

    return () => {
      if (isLoading.value) {
        return (
          <div class="w-full min-h-screen flex items-center justify-center">
            <LoadingComponent />
          </div>
        );
      }

      if (error.value) {
        return (
          <div class="container mx-auto p-4 max-w-2xl">
            <div class="alert alert-error">
              <span>
                {t("customer.checkout_result.error_loading_orders", [
                  error.value,
                ])}
              </span>
            </div>
            <button class="btn btn-primary mt-4" onClick={handleBackToMenu}>
              {t("customer.checkout_result.back_to_menu")}
            </button>
          </div>
        );
      }

      return (
        <div class="min-h-screen">
          {/* Success Header */}
          <div class="px-4 py-2">
            <div
              style={`background-image: url("${getRestaurantImageURL(
                shop.value,
              )}");`}
              class="bg-no-repeat bg-cover bg-center flex justify-center items-center py-2"
            >
              <div class="bg-[rgba(255,255,255,0.8)] mx-auto px-4 py-8 sm:py-12 rounded-3xl rounded rounded-xl shadow-xl border w-full">
                <div class="text-center w-full">
                  {/* Success Icon */}
                  <div class="mb-6 w-full">
                    <div class="w-full h-20 mx-auto bg-success-content/20 rounded-full flex items-center justify-center">
                      <span
                        style="font-size: 120px;"
                        class="material-symbols-outlined text-success text-6xl "
                      >
                        check_circle
                      </span>
                    </div>
                  </div>

                  <h1 class="text-3xl text-accent sm:text-4xl font-bold mb-3">
                    {t("customer.checkout_result.order_confirmed")}
                  </h1>
                  <p class="text-lg  mb-4">
                    {t("customer.checkout_result.good_choice")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div class="container mx-auto px-4 py-4">
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {/* Left Column - Order Details */}
              <div class="lg:col-span-2">
                {orders.value.length > 0 ? (
                  <div class="">
                    {/* User's Orders - Prominent */}
                    {userOrders.value.length > 0 && (
                      <div class="space-y-4">
                        <div class="flex items-center gap-2">
                          <h2 class="text-2xl font-bold text-accent">
                            {t(
                              userOrders.value.length === 1
                                ? "customer.checkout_result.your_order"
                                : "customer.checkout_result.your_orders",
                            )}
                          </h2>
                          <div class="badge badge-primary badge-lg">
                            {userOrders.value.length}
                          </div>
                        </div>
                        <div class="space-y-4">
                          {userOrders.value.map((order, index) => (
                            <div key={order.id || index} class="">
                              <OrderReceipt order={order} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Other Group Members' Orders */}
                    {otherOrders.value.length > 0 && (
                      <div class="space-y-4">
                        <div class="flex items-center gap-2">
                          <span class="material-symbols-outlined text-base-content/60">
                            group
                          </span>
                          <h3 class="text-xl font-semibold text-accent">
                            {t("customer.checkout_result.group_orders")}
                          </h3>
                          <div class="badge badge-neutral badge-sm">
                            {otherOrders.value.length}
                          </div>
                        </div>
                        <div class="space-y-4 ">
                          {otherOrders.value.map((order, index) => (
                            <div key={order.id || index} class="">
                              <OrderReceipt order={order} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div class="bg-base-200 rounded rounded-xl shadow-xl border">
                    <div class="card-body text-center py-12">
                      <div class="avatar placeholder mb-4">
                        <div class="bg-base-200 text-base-content/50 rounded-full w-16">
                          <span class="material-symbols-outlined text-2xl">
                            search_off
                          </span>
                        </div>
                      </div>
                      <p class="text-base-content/70 text-lg">
                        {t("customer.checkout_result.no_orders_found")}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - Actions & Info */}
              <div class="space-y-6 hidden lg:block">
                {/* Order Status */}
                <div class="bg-base-200 rounded rounded-xl shadow-xl border">
                  <div class="card-body">
                    <h3 class="card-title mb-4">
                      {t("customer.checkout_result.order_status")}
                    </h3>

                    <div class="space-y-3">
                      <div class="flex items-center gap-3">
                        <div class="w-3 h-3 bg-success rounded-full"></div>
                        <span class="text-sm">
                          {t("customer.checkout_result.status_confirmed")}
                        </span>
                      </div>
                      <div class="flex items-center gap-3">
                        <div class="w-3 h-3 bg-warning rounded-full animate-pulse"></div>
                        <span class="text-sm">
                          {t("customer.checkout_result.status_preparing")}
                        </span>
                      </div>
                      <div class="flex items-center gap-3">
                        <div class="w-3 h-3 bg-base-300 rounded-full"></div>
                        <span class="text-sm text-base-content/50">
                          {t("customer.checkout_result.status_ready")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Information */}
                <div class="bg-base-200 rounded rounded-xl shadow-xl border ">
                  <div class="card-body">
                    <h3 class="card-title mb-4 flex items-center gap-2">
                      <span class="material-symbols-outlined text-info">
                        info
                      </span>
                      {t("customer.checkout_result.whats_next")}
                    </h3>

                    <p class="text-sm text-base-content/70 leading-relaxed">
                      {t("customer.checkout_result.whats_next_copy")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    };
  },
});
